import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { GiftBoxRow } from '@models/gift';
import { useFlow } from '@stackflow/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    giftBox: GiftBoxRow;
}

function TransactionItem({ giftBox }:Props) {
    const {
        status, itemId, itemName, senderName, giftBoxType, receiverName, imageUrl, giftItemType,
    } = giftBox;
    const { t } = useTranslation('gift');
    const badge = (() => {
        switch (status) {
        case 'EXPIRED':
            return (<StatusBadge>
                <Text size='s' fontWeight='bold'
                    color={colors.white}>{t('giftBox.status.expired')}</Text>
            </StatusBadge>);
        case 'CANCELED':
            return (<StatusBadge>
                <Text size='s' fontWeight='bold'
                    color={colors.white}>{t('giftBox.status.canceled')}</Text>
            </StatusBadge>);
        case 'USED':
            return (<UsedBadge>
                <Text size='s' fontWeight='bold'
                    color={colors.white}>{t('giftBox.status.used')}</Text>
            </UsedBadge>);
        default:
            return <Fragment/>;
        }
    })();
    const { push } = useFlow();
    const handleClick = () => {
        if (giftBoxType === 'RECEIVED') {
            push('GiftReceivedActivity', { giftBoxItemId: itemId, giftItemType });
        } else {
            push('GiftSentActivity', { giftBoxItemId: itemId, giftItemType });
        }
    };
    return (<Wrapper onClick={handleClick} >
        <Container aria-label='transation-item'
            fullWidth>
            <ImageBox isInactive={status === 'EXPIRED' || status === 'CANCELED'}>
                <Image src={imageUrl}
                    width={120}
                    height={120}
                    alt='' />
                {badge}
            </ImageBox>
            <Footer fullWidth
                spacing={4}
                align='center'>
                <Text size='lg' fontWeight='bold'>{itemName}</Text>
                {giftBoxType === 'RECEIVED' && (
                    <Text>{t('giftBox.from', { name: senderName })}</Text>
                )}
                {giftBoxType === 'SENT' && (
                    <Text>{t('giftBox.to', { name: receiverName })}</Text>
                )}
            </Footer>
        </Container>
    </Wrapper>);
}

const Wrapper = styled.div`
    width: 100%;
    overflow: hidden;
    border: 1px solid var(--color-gray2);
    border-radius: 12px;
    box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.10);
`;

const Container = styled(Column)`
    overflow: hidden;
`;

const inactiveBackground = css`
    background-color: rgba(196, 131, 114, 0.15);
`;

const ImageBox = styled.div<{
    isInactive: boolean;
}>`
    display: flex;
    padding: 10px 14px 16px;
    position: relative;
    width: 100%;
    align-items: center;
    justify-content: center;
    ${({ isInactive }) => isInactive && inactiveBackground}
`;

const Footer = styled(Column)`
    background-color: var(--color-gray1);
    padding: 8px 0 10px;
`;

const badgeBase = css`
    border-radius: 0 12px 0 0;
    padding: 2px 6px;
    position: absolute;
`;

const StatusBadge = styled.div`
    ${badgeBase}
    background-color: var(--color-primary);
    right: 1px;
    top: 1px;
`;

const UsedBadge = styled.div`
    ${badgeBase}
    background-color: var(--color-gray7);
    right: 0;
    top: 0;
`;

export default TransactionItem;
